import { Button } from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import React from "react";

import type { DistributionMode } from "./types";

// why: Button's label slot is `whitespace-nowrap`; these link actions are
// full sentences that must wrap inside a narrow position card on mobile.
// `button-label` is a documented `data-slot`, so this is the supported hook.
// `self-start` keeps each link beside its text. The minimum height retains
// the mobile and desktop text line boxes.
const LINK_WRAP =
    "self-start min-h-5 md:min-h-6 text-left [&_[data-slot=button-label]]:whitespace-normal";

interface YaasPositionsFooterProps {
    /** Advertised APY when available. A missing rate uses a general
     * comparison link and does not block the account actions. */
    yaasApy: number | null;
    /** Footer overall opacity gate. The page hides the footer during
     *  a card transition and fades it in once the action card has settled. */
    cardSettled: boolean;
    /** User's current distribution mode — drives the
     *  Switch-to-other-mode link's copy (Claim ↔ Stream). */
    currentDistributionMode: DistributionMode;
    /** Flips the user's distribution mode to the opposite of
     *  `currentDistributionMode`. Replaces the old Manage Positions
     *  button now that mode-switching is the only thing that surface
     *  did — surfacing it as text demotes it from a primary action
     *  to a lightweight toggle. Optional — omitted, the link does not
     *  render. */
    onSwitchDistributionMode?: () => void;
    /** Opens the Comparison Explorer overlay (charts, benchmark
     *  comparison). */
    onOpenExplore: () => void;
    /** Whether the account has any transactions to show. The link is
     *  omitted, not disabled, when it would open an empty list. */
    hasTransactions?: boolean;
    /** Opens the Transactions / distributions history overlay.
     *  Rendered as a secondary link below the APY-compares line. */
    onOpenTransactions?: () => void;
    /** Current routing mode — drives the switch link's copy, the same
     *  way `currentDistributionMode` drives the link above it. */
    advancedRouting?: boolean;
    /** Flips between single-destination and multiple-destination
     *  routing. This replaced the "Advanced Mode" toggle that used to
     *  sit in the action row: a switch there silently rewired the edit
     *  affordance two rows above it. As a link here it sits with the
     *  other "change how this works" actions and says what it does.
     *  Optional — omitted, the link does not render. */
    onSwitchRoutingMode?: () => void;
}

/**
 * Footer rendered below the retail YaaS right-panel action card.
 * Contains three stacked links:
 *   1. "See how X% APY compares" — opens the Comparison Explorer.
 *   2. "Switch to stream / claim rewards…" — flips the user's
 *      distribution mode. Copy depends on the current mode. Only
 *      rendered when `onSwitchDistributionMode` is supplied.
 *   3. "Switch to multiple / single destination routing" — flips the
 *      routing mode. Only rendered when `onSwitchRoutingMode` is
 *      supplied.
 *   4. "See account transaction history" — opens the distributions /
 *      transaction history overlay.
 *
 * Reaching /venues is deliberately NOT here — the inline "Additional
 * Earnings in Venues" row in the positions breakdown already carries
 * that path, with a rate attached, and two entry-points to the same
 * page competed.
 *
 * Configure Rewards Routing used to live here too but moved into a
 * primary "Rewards Routing →" button next to Swap USDv above the
 * footer — routing is heavy enough that it deserves the raised slot.
 *
 * A missing APY can be a completed response, not a pending request.
 * Keep actions available and use a comparison label without a rate.
 *
 * Layout is always mounted (height auto + 48px marginTop) so the
 * column total is stable across card transitions; opacity drives
 * visibility, not mount/unmount, to avoid re-centering jumps.
 *
 * Shared by the retail and simulation panels.
 */
export function YaasPositionsFooter({
    yaasApy,
    cardSettled,
    currentDistributionMode,
    onSwitchDistributionMode,
    onOpenExplore,
    hasTransactions = true,
    onOpenTransactions,
    advancedRouting = false,
    onSwitchRoutingMode
}: YaasPositionsFooterProps): React.ReactElement {
    return (
        <motion.div
            key="footer"
            initial={{ opacity: 0, y: 0 }}
            animate={{
                // No y translate — the panel's form-content + Submit
                // smoothly shrink at Beat 1 (300ms), which moves the
                // action_wrapper bounds and shifts footer.layout.top
                // up; the footer's visual position tracks the panel
                // naturally. By the time the footer fades in (at
                // count-up start), the panel is done shrinking and
                // the footer is at its final position.
                opacity: cardSettled ? 1 : 0
            }}
            transition={{
                opacity: {
                    duration: 0.2,
                    ease: "easeOut"
                }
            }}
            className="flex flex-col mt-6 md:mt-12"
        >
            <div className="flex flex-col gap-1">
                <Button
                    variant="link"
                    size="sm"
                    onClick={onOpenExplore}
                    className={LINK_WRAP}
                >
                    {yaasApy !== null
                        ? `See how ${yaasApy.toFixed(1)}% APY compares`
                        : "Compare USDv rates"}
                </Button>
                {onSwitchDistributionMode && (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={onSwitchDistributionMode}
                        className={LINK_WRAP}
                    >
                        {currentDistributionMode === "claim"
                            ? "Switch to stream rewards directly to you"
                            : "Switch to claim rewards when you want"}
                    </Button>
                )}
                {onSwitchRoutingMode && (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={onSwitchRoutingMode}
                        className={LINK_WRAP}
                    >
                        {advancedRouting
                            ? "Switch to single destination routing"
                            : "Switch to multiple destinations routing"}
                    </Button>
                )}
                {hasTransactions && onOpenTransactions && (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={onOpenTransactions}
                        className={LINK_WRAP}
                    >
                        See account transaction history
                    </Button>
                )}
            </div>
        </motion.div>
    );
}
